import {
  ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AuthPage,
  CX_PRO,
  getMFEConfig,
  getSplatUserType,
  THIRD_PARTY_RETAIL_ADMIN,
} from "@sunrun/sales-experience-shared";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EVENT_BUS, getNewPath, useIframeMessages } from "./common";
import { postMessage } from "./helpers";
import { consumeDeepLink } from "./deepLink";
import type { IFrame } from "components/atoms/IFrame";
import { Message } from "messages";
import { useUserProfile } from "providers/UserProfile";

type UseAuthAdapterReturn = ComponentProps<typeof IFrame> & {
  isLoaded: boolean;
  showAuth: boolean;
  isOktaToken: boolean;
};
const useAuthAdapter = (page: AuthPage): UseAuthAdapterReturn => {
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [showAuth, setShowAuth] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOktaToken, setIsOktaToken] = useState(false);
  const { completeProfile } = useUserProfile();
  const [loginSuccess, setLoginSuccess] = useState(false);

  const ref = useRef<HTMLIFrameElement | null>(null);
  const config = getMFEConfig("auth", page);
  const expectedParams = new URLSearchParams();
  const sfdcToken = searchParams.get("sfdcToken");
  const sfdcRefreshToken = searchParams.get("sfdcRefreshToken");
  const oktaToken = searchParams.get("oktaToken");
  if (sfdcToken) {
    expectedParams.set("sfdcToken", sfdcToken);
    if (sfdcRefreshToken) {
      expectedParams.set("sfdcRefreshToken", sfdcRefreshToken);
    }
  }
  if (oktaToken) {
    expectedParams.set("oktaToken", oktaToken);
  }
  const code = searchParams.get("code");
  if (code) {
    expectedParams.set("code", code);
  }
  const src = `${config.iFrameUrl}?${expectedParams}`;

  const busQueryAuthTokenHandler = useCallback(
    (message: Message) => {
      postMessage(message, ref);
      setShowAuth(true);
    },
    [setShowAuth]
  );
  const busCommandLogoutHandler = useCallback(
    (message: Message) => {
      postMessage(message, ref);
      setShowAuth(true);
      setIsOktaToken(false);
    },
    [setShowAuth]
  );
  const busCommandMasqueradeHandler = useCallback((message: Message) => {
    postMessage(message, ref);
  }, []);
  useEffect(() => {
    EVENT_BUS.on("QUERY:AUTH_TOKEN", busQueryAuthTokenHandler);
    EVENT_BUS.on("COMMAND:LOGOUT", busCommandLogoutHandler);
    EVENT_BUS.on("COMMAND:MASQUERADE", busCommandMasqueradeHandler);
    return () => {
      EVENT_BUS.off("QUERY:AUTH_TOKEN", busQueryAuthTokenHandler);
      EVENT_BUS.off("COMMAND:LOGOUT", busCommandLogoutHandler);
      EVENT_BUS.off("COMMAND:MASQUERADE", busCommandMasqueradeHandler);
    };
  }, [
    busCommandLogoutHandler,
    busQueryAuthTokenHandler,
    busCommandMasqueradeHandler,
  ]);
  useIframeMessages(ref, "Auth", "/", (message) => {
    if (!message) return;
    switch (message.type) {
      case "EVENT:AUTH_TOKEN":
        setShowAuth(false);
        setIsOktaToken(message.payload.tokenType === "okta");
        break;
      case "EVENT:READY":
        busQueryAuthTokenHandler({
          source: "Container",
          type: "QUERY:AUTH_TOKEN",
        });
        setIsLoaded(true);
        break;
      case "EVENT:LOGIN_SUCCESS":
        setLoginSuccess(true);
        break;
    }
    return message;
  });
  useEffect(() => {
    if (JSON.stringify(completeProfile) !== "{}" && loginSuccess) {
      let defaultPage = "/dashboard";
      const splatUserType = getSplatUserType(completeProfile);
      if (splatUserType === THIRD_PARTY_RETAIL_ADMIN) {
        defaultPage = "/retail-admin";
      } else if (splatUserType === CX_PRO) {
        defaultPage = "/pipeline";
      }
      navigate(getNewPath(consumeDeepLink() ?? defaultPage));
      setLoginSuccess(false);
    }
  }, [completeProfile, loginSuccess, navigate]);
  return {
    ref,
    basePath: src,
    isLoaded,
    src,
    showAuth: page === "splatLogin" ? true : showAuth,
    isOktaToken,
    title: config.title,
  };
};

export { useAuthAdapter };
